import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Clock, CheckCircle, XCircle, Search, Eye } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth, API } from '../App';
import axios from 'axios';

export default function TrainerApplicationsDetail() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchApplications = async () => {
      try {
        const profileRes = await axios.get(`${API}/trainers/me`, { headers: { Authorization: `Bearer ${token}` } });
        const appsRes = await axios.get(`${API}/applications/trainer/${profileRes.data.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setApplications(appsRes.data);
      } catch (err) { console.error(err); }
      setLoading(false);
    };
    fetchApplications();
  }, [token]);

  const filtered = applications.filter(a => {
    if (statusFilter !== 'all' && a.status !== statusFilter) return false;
    if (searchTerm && !(a.job_title || '').toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  const statusCounts = {
    all: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    accepted: applications.filter(a => a.status === 'accepted').length,
    rejected: applications.filter(a => a.status === 'rejected').length,
    invited: applications.filter(a => a.status === 'invited').length,
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/trainer/dashboard')} className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors" data-testid="back-to-dashboard">
          <ArrowLeft className="w-4 h-4" /> Back to Dashboard
        </button>

        <div className="mb-8">
          <h1 className="font-outfit text-3xl font-bold text-slate-900" data-testid="page-title">My Applications</h1>
          <p className="text-slate-500 mt-1">Track all your job applications and their status</p>
        </div>

        {/* Status Filter Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          {[
            { label: 'All', count: statusCounts.all, filter: 'all' },
            { label: 'Pending', count: statusCounts.pending, filter: 'pending' },
            { label: 'Accepted', count: statusCounts.accepted, filter: 'accepted' },
            { label: 'Invited', count: statusCounts.invited, filter: 'invited' },
            { label: 'Rejected', count: statusCounts.rejected, filter: 'rejected' },
          ].map(c => (
            <div key={c.filter} onClick={() => setStatusFilter(c.filter)}
              className={`bg-white rounded-xl border p-4 cursor-pointer transition-all ${statusFilter === c.filter ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200 hover:border-slate-300'}`}
              data-testid={`filter-${c.filter}`}>
              <span className="text-sm text-slate-500">{c.label}</span>
              <div className="font-outfit text-2xl font-bold text-slate-900 mt-1">{c.count}</div>
            </div>
          ))}
        </div>

        {/* Search */}
        <div className="flex gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input placeholder="Search by job title..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10" data-testid="search-applications" />
          </div>
        </div>

        {/* Applications Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Job Title</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Applied On</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Status</th>
                  <th className="text-left p-4 text-sm font-medium text-slate-600">Cover Note</th>
                  <th className="text-right p-4 text-sm font-medium text-slate-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.length === 0 ? (
                  <tr><td colSpan={5} className="p-12 text-center text-slate-500">No applications found</td></tr>
                ) : filtered.map(app => (
                  <tr key={app.id} className="hover:bg-slate-50 transition-colors" data-testid={`app-row-${app.id}`}>
                    <td className="p-4">
                      <div className="font-medium text-slate-900">{app.job_title || 'Untitled Job'}</div>
                    </td>
                    <td className="p-4 text-sm text-slate-500">{new Date(app.applied_at).toLocaleDateString()}</td>
                    <td className="p-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full font-medium ${
                        app.status === 'accepted' ? 'bg-emerald-50 text-emerald-700' :
                        app.status === 'rejected' ? 'bg-red-50 text-red-700' :
                        app.status === 'invited' ? 'bg-purple-50 text-purple-700' :
                        'bg-amber-50 text-amber-700'
                      }`}>
                        {app.status === 'accepted' && <CheckCircle className="w-3 h-3" />}
                        {app.status === 'rejected' && <XCircle className="w-3 h-3" />}
                        {app.status === 'pending' && <Clock className="w-3 h-3" />}
                        {app.status}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-slate-600 max-w-xs truncate">{app.cover_letter || '-'}</td>
                    <td className="p-4 text-right">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/job/${app.job_id}`)} data-testid={`view-job-${app.job_id}`}>
                        <Eye className="w-3 h-3 mr-1" /> View Job
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-slate-50 border-t text-xs text-slate-500">Showing {filtered.length} of {applications.length} applications</div>
        </div>
      </div>
    </div>
  );
}
